import { InfoMessage } from "components/shared/InfoMessage";
import graphql from "babel-plugin-relay/macro";
import { openConvergeLightbox } from "components/payment/Elavon/Converge/converge";
import { convergeMessages } from "components/payment/Elavon/Converge/convergeMessages";
import { PaymentFailedMessage } from "components/payment/PaymentFailedMessage";
import { IPaymentFormChildProps } from "components/payment/PaymentForm";
import { StandardPaymentButtonLayout } from "components/payment/StandardPaymentButtonLayout";
import { paymentMessages } from "domain/payment/paymentMessages";
import { ConvergeProvider_configuration } from "generatedQueries/ConvergeProvider_configuration.graphql";
import isEqual from "lodash/isEqual";
import React, { FC, useEffect, useState } from "react";
import { useIntl } from "react-intl";
import { createFragmentContainer, useRelayEnvironment } from "react-relay";
import useScript from "react-script-hook";
import { BasePaymentButton } from "components/payment/BasePaymentButton";

interface IGraphQlProps {
    configuration: ConvergeProvider_configuration;
}

interface IProps {
    termsAndConditionsAccepted: boolean;
    allPaymentRowsAreValid: boolean;
}

type Props = IProps & IPaymentFormChildProps & IGraphQlProps;

const ConvergeProviderInternal: FC<Props> = ({
    configuration,
    paymentReady,
    requestState,
    errorMessages,
    termsAndConditionsAccepted,
    allPaymentRowsAreValid,
    amountToCharge,
    submitPayment,
    isPaymentProcessing: isPaymentFormProcessing,
}) => {
    const environment = useRelayEnvironment();
    const { formatMessage } = useIntl();

    const [isConvergePaymentProcessing, setIsConvergePaymentProcessing] = useState(false);
    const [hasErrorOccurred, setHasErrorOccurred] = useState(false);

    const [isConvergeScriptLoading, convergeScriptError] = useScript({
        src: configuration.elavonConvergeLibraryUri,
        checkForExisting: true,
        async: "true",
    });

    useEffect(() => {
        if (convergeScriptError) {
            setHasErrorOccurred(true);
        }
    }, [convergeScriptError]);

    useEffect(() => {
        const paymentMethodRequest = isConvergeScriptLoading ? {
            convergePaymentRequest: {
                token: "",
            },
        } : null;

        if (!isEqual(paymentMethodRequest, requestState)) {
            paymentReady(paymentMethodRequest);
        }
    }, [paymentReady, requestState, isConvergeScriptLoading]);

    const isPaymentValid = termsAndConditionsAccepted && allPaymentRowsAreValid && amountToCharge > 0;
    const isPaymentButtonDisabled = isConvergeScriptLoading || isConvergePaymentProcessing || !isPaymentValid || isPaymentFormProcessing;
    const isProcessing = isConvergePaymentProcessing || isPaymentFormProcessing;

    const disabledReason = isPaymentButtonDisabled && !isProcessing
        ? formatMessage(paymentMessages.paymentButtonDisabledWarning)
        : undefined;

    return (
        <>
            <InfoMessage message={convergeMessages.convergeWaitLabel} />
            <StandardPaymentButtonLayout
                payButton={
                    <BasePaymentButton
                        amount={amountToCharge}
                        currency={configuration.currency}
                        isDisabled={isPaymentButtonDisabled}
                        isProcessing={isProcessing}
                        onClick={onPaymentButtonClicked}
                        disabledReason={disabledReason}
                    />
                }
                errorMessages={errorMessages}
            />
            {
                hasErrorOccurred &&
                <PaymentFailedMessage code={"ERROR"} organisationContact={configuration.organisationContact} />
            }
        </>
    );

    function onPaymentButtonClicked() {
        setIsConvergePaymentProcessing(true);
        openConvergeLightbox(environment, onPaymentSessionResponse, onPaymentSessionError, onPaymentSessionCancel, amountToCharge);
    }

    function onPaymentSessionResponse(response: IConvergeTokenResponse) {
        setIsConvergePaymentProcessing(false);
        submitPayment({
            convergePaymentRequest: {
                token: response.ssl_token,
            },
        });
    }

    function onPaymentSessionError() {
        setIsConvergePaymentProcessing(false);
        setHasErrorOccurred(true);
    }

    function onPaymentSessionCancel() {
        setIsConvergePaymentProcessing(false);
    }
};

export const ConvergeProvider = createFragmentContainer(ConvergeProviderInternal, {
    configuration: graphql`
        fragment ConvergeProvider_configuration on Configuration {
            elavonConvergeLibraryUri
            currency
            organisationContact
        }`,
});
